import type { Answers, QuestionnaireState } from "../types";
import type { FlattenedSectionProps } from "../../../components/Questionnaire/Section";
import { createValidator } from "../../../components/Questionnaire/validation/form";
import type { PartialQuestion } from "../../../components/Questionnaire/validation/form/types";
import type { ValidationResponse } from "../../../components/Questionnaire/validation/question/rules/types";
import type { Locale } from "../../../types";

export const getQuestionList = (
  sections: FlattenedSectionProps[],
  currentSectionIndex?: number,
) => {
  if (currentSectionIndex === undefined) {
    return sections.reduce(
      (acc, section) => acc.concat(section.questions),
      [] as PartialQuestion[],
    );
  }

  const completedSections = sections.slice(0, currentSectionIndex + 1);

  return completedSections.reduce(
    (acc, section) => acc.concat(section.questions),
    [] as PartialQuestion[],
  );
};

export const getFirstInvalidSection = (
  results: ValidationResponse[],
  sections: FlattenedSectionProps[],
) => {
  if (results.length === 0) {
    return undefined;
  }

  const invalidQuestionIds = results.map(({ id }) => id);

  return sections.findIndex(section =>
    section.questions.some(({ name }) => invalidQuestionIds.includes(name)),
  );
};

export const validateAction = ({
  answers,
  sections: sectionsFromState,
  validations,
  visibility,
  currentSectionIndex,
  lastViewedSectionIndex,
  config,
  asyncErrors,
  groups,
  mode,
}: QuestionnaireState): {
  errors: Record<string, string>;
  firstInvalidSectionIndex?: number;
  isValid: boolean;
} => {
  const sections = sectionsFromState as unknown as FlattenedSectionProps[];
  const isStackedSections = mode === "stacked-sections";

  // Determine which sections to validate:
  // - For stacked-sections mode: validate ALL sections (user can see all at once)
  // - For standard mode: validate up through the section the user has seen
  const sectionIndexToValidateUpTo = isStackedSections
    ? undefined // undefined means validate all sections
    : Math.max(lastViewedSectionIndex, currentSectionIndex);

  const questionList = getQuestionList(sections, sectionIndexToValidateUpTo);

  if (questionList.length === 0) {
    return { errors: {}, isValid: false };
  }

  // Define validation function
  const validateQuestion = createValidator(validations || []);
  const validateQuestions = (
    questions: PartialQuestion[],
    answers: Answers,
    visibility: Record<string, boolean>,
    locale?: string,
  ) =>
    questions
      .map(question =>
        validateQuestion({
          question,
          answers,
          visibilityMap: visibility,
          locale,
          groups,
        }),
      )
      .filter(({ valid }) => !valid);

  const results = validateQuestions(
    questionList,
    answers,
    visibility!,
    config?.locale as Locale,
  );

  // Map validation results to errors object immutably
  const validationErrors = results.reduce(
    (acc, { id, message }) => ({
      ...acc,
      [id]: message!,
    }),
    {} as Record<string, string>,
  );

  // Merge validation errors and async errors immutably
  const allErrors = {
    ...validationErrors,
    ...asyncErrors,
  };

  // Determine if form is valid
  const isValid = Object.keys(allErrors).length === 0;

  // Create combined results for finding first invalid section
  const asyncErrorResults = Object.keys(asyncErrors).map(id => ({
    id,
    message: asyncErrors[id],
    valid: false,
  }));
  const allResults = [...results, ...asyncErrorResults];

  // If results is not empty, get section index of first error
  const firstInvalidSectionIndex = getFirstInvalidSection(allResults, sections);

  return {
    isValid,
    errors: allErrors,
    firstInvalidSectionIndex,
  };
};
